import { useEffect, useState } from 'react';
import { Link, useRecordContext, type RaRecord } from 'react-admin';
import { Box, Button, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { SectionCard } from '../../shared/Panel';
import { shortDateTime } from '../../shared/dateTime';
import { TABLE_SCROLL_SX } from '../../shared/tableScroll';
import { toErrorMessage } from '../userOffers/orderActionsApi';
import { fetchVehicleHistory, type VehicleHistory } from './vehiclePlanApi';

/**
 * „Historia pojazdu” na karcie auta (15.10.2026, makieta FTL p. Mariusza
 * 13.10, PROMPT.md pkt 9.2 „historia zmian”; wzorzec 13.10: „Kiedy / Co /
 * Przed / Po / Kto / Powód”; Etap 1 — decyzja Bartka 15.10.2026; przełącznik
 * `vehicleAssignmentHistory`).
 *
 * Obsadzenia tym autem, zmiany pojazdu, odpięcia, dodane i zwolnione warianty
 * — z dzienników zleceń (API `vehicle-schedules/vehicle-history.ts`), nic
 * nowego się nie zapisuje. Zmiany samej kartoteki z TMS (zestaw,
 * eksploatacja, kierowca…) są w osobnej sekcji „Historia zmian z TMS”.
 * Sekcja zwinięta, dane dopiero po rozwinięciu — jak historia z TMS.
 */
export const CarVehicleHistory = () => {
  const r = useRecordContext<RaRecord & Record<string, unknown>>();
  const carId = r?.id;
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<VehicleHistory | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || view !== null || carId === undefined || carId === null) return undefined;
    let cancelled = false;
    setError(null);
    fetchVehicleHistory(carId)
      .then((v) => {
        if (!cancelled) setView(v);
      })
      .catch((e) => {
        if (!cancelled) setError(toErrorMessage(e));
      });
    return () => {
      cancelled = true;
    };
  }, [open, view, carId]);

  if (!r) return null;
  const entries = view?.entries ?? [];

  return (
    <SectionCard title="Historia pojazdu: obsadzenia i warianty">
      <Button size="small" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        {open ? 'Zwiń' : 'Pokaż'}
      </Button>
      {open && (
        <Box sx={{ mt: 1 }} data-testid="car-vehicle-history">
          {error && (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          )}
          {!error && view === null && (
            <Typography variant="body2" color="text.secondary">
              Wczytywanie…
            </Typography>
          )}
          {view !== null && entries.length === 0 && (
            <Typography variant="body2">
              Brak wpisów. Pojawią się po obsadzeniu, odpięciu albo dodaniu wariantu tego pojazdu.
            </Typography>
          )}
          {entries.length > 0 && (
            <Box sx={TABLE_SCROLL_SX}>
              <Table size="small" aria-label="Historia pojazdu">
                <TableHead>
                  <TableRow>
                    <TableCell>Kiedy</TableCell>
                    <TableCell>Zlecenie</TableCell>
                    <TableCell>Co</TableCell>
                    <TableCell>Przed</TableCell>
                    <TableCell>Po</TableCell>
                    <TableCell>Kto</TableCell>
                    <TableCell>Powód</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {entries.map((e) => (
                    <TableRow key={e.id} data-testid="vehicle-history-row" data-kind={e.kind}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{shortDateTime(e.at)}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Link to={`/user-offers/${e.orderId}/show`}>{e.orderNumber ?? `#${e.orderId}`}</Link>
                      </TableCell>
                      <TableCell>{e.label}</TableCell>
                      <TableCell>{e.before ?? '—'}</TableCell>
                      <TableCell>{e.after ?? '—'}</TableCell>
                      <TableCell>{e.who ?? '—'}</TableCell>
                      <TableCell>{e.reason ?? '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
          {view !== null && view.limit > 0 && entries.length >= view.limit && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Pokazano {view.limit} najnowszych wpisów.
            </Typography>
          )}
        </Box>
      )}
    </SectionCard>
  );
};
